<!-- 🎯 SUPERADMIN DASHBOARD - Plantilla Canvas Exacta -->
<template>
  <v-layout>
    <!-- 🧭 Sidebar Navigation -->
    <SidebarNavigation
      :collapsed="sidebarCollapsed"
      @toggle="sidebarCollapsed = !sidebarCollapsed"
    />

    <!-- Main content (offset automático por v-layout) -->
    <v-main>
      <div class="min-h-screen bg-gray-50 p-6" data-testid="role-home-super_admin">
    <div class="space-y-6">
      <!-- Header -->
      <header class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <div class="h-9 w-9 rounded-xl bg-gray-900 text-white flex items-center justify-center text-sm font-bold">
            MGI
          </div>
          <div>
            <h1 class="text-2xl font-bold leading-tight">SuperAdmin Dashboard</h1>
            <p class="text-sm text-gray-500 -mt-0.5">Rol: Acceso total • Vista ejecutiva BI</p>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <!-- Campana integrada al flujo del header -->
          <NotificationBell />
        </div>
      </header>

      <!--
        P13-V5A-R3 · four visible truth states, never interchangeable:
        loading · error (the canonical reader failed — NO figures are shown) · a real value
        (zero included) · an explicit absence. There is no fifth state in which a value is invented.
      -->
      <section
        v-if="error"
        data-testid="superadmin-kpis-error"
        class="rounded-2xl border border-red-300 bg-red-50 p-4 text-center"
      >
        <div class="text-2xl">⚠️</div>
        <div class="font-semibold text-red-700 mt-1">No se pudieron obtener los indicadores</div>
        <div class="text-sm text-red-600">{{ error }}</div>
        <div class="text-xs text-red-500 mt-2">
          No se muestran cifras: un fallo de origen no se sustituye por un valor estimado.
        </div>
      </section>

      <!-- KPIs críticos -->
  <section v-else class="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="superadmin-kpis">
        <template v-if="!loading">
          <div
            v-for="k in kpiCards"
            :key="k.title"
            data-testid="superadmin-kpi-card"
            :data-metric-state="k.state"
            class="rounded-2xl border p-4 text-center shadow-sm"
            :style="{ backgroundColor: toneStyles[k.tone].bg, borderColor: toneStyles[k.tone].border }"
          >
            <div class="text-2xl">{{ k.icon }}</div>
            <div class="text-gray-600 mt-1">{{ k.title }}</div>
            <!-- P13-V5A-R5: addressable so a journey can assert the EXACT figure rather than a
                 substring, which '0' shares with '10' and '100%'. Presentation unchanged. -->
            <div class="text-2xl font-bold" data-testid="superadmin-kpi-value" :class="k.state === 'NO_DATA' ? 'text-gray-400 italic' : ''">{{ k.value }}</div>
            <div class="text-sm" :style="{ color: toneStyles[k.tone].text }">{{ k.subtitle }}</div>
          </div>
        </template>
        <template v-else>
          <div v-for="i in 4" :key="i" class="rounded-2xl border p-4 text-center shadow-sm animate-pulse bg-gray-100">
            <div class="h-6 bg-gray-200 rounded mb-2"></div>
            <div class="h-4 bg-gray-200 rounded mb-2"></div>
            <div class="h-6 bg-gray-300 rounded"></div>
          </div>
        </template>
      </section>

      <!-- Mapa + Productividad -->
      <section class="grid gap-6 lg:grid-cols-3">
        <!-- Mapa (reutilización del componente Interactivo) -->
        <div class="lg:col-span-2 h-80 rounded-2xl border bg-white shadow-sm overflow-hidden">
          <InteractiveMapMinimal />
        </div>

        <!-- Productividad -->
    <div class="h-80 rounded-2xl border bg-white shadow-sm p-4">
          <!--
            P13-V7B · the LABEL now names what the rows are. The panel was titled "Productividad por
            Administración — Últimos 30 días", but every row is a DEPARTMENT (`department_name`) and
            its figure is that department's closing rate (`completion_rate`) over the whole
            authorized partition: the producer applies no time window at all. Both the axis and
            the 30 days were statements with nothing behind them.
          -->
          <h2 class="font-semibold mb-1">Tasa de cierre por departamento</h2>
          <p class="text-xs text-gray-500 mb-3">Cerradas sobre el total, en la partición autorizada</p>
          <div class="space-y-4">
      <div v-if="productividades.length === 0 && !loading" class="text-xs text-gray-500">Sin datos</div>
      <div v-for="r in productividades" :key="r.label">
              <div class="flex justify-between text-sm mb-1">
                <span>{{ r.label }}</span>
                <span>{{ r.value }}%</span>
              </div>
              <div class="h-3 rounded-full bg-gray-200 overflow-hidden">
                <div class="h-full rounded-full" :style="{ width: r.value + '%', background: '#4F46E5' }"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

  <!-- Fila secundaria: Zonas + Analytics (siempre visible en 2 columnas) -->
  <section class="grid grid-cols-2 gap-6">
        <div class="rounded-2xl border bg-white shadow-sm p-4">
          <!--
            W2 · REPORTING-PAYLOAD-EXCESS-API-ANALYTICS · THE SERIES IS NOW INCIDENCE-ONLY.

            P13-V5A-R5 repaired this panel's LABEL to match the field it rendered
            (`workload_per_member`, incidences per team member) and said explicitly that swapping
            the series would be a product decision, not a truth repair. W2 is that product
            decision: R3 classified `workload_per_member` as UNCONTRACTED_CAPABILITY — workforce
            capacity the signed first-pilot Reporting clause does not name — and the backend
            projection removes it from `GET /api/analytics/departments`.

            So the panel now ranks departments by `open_incidents` from the same signed response:
            how many incidences each department currently has open inside the authorized
            partition. The unit is stated in the row, as R5 established. The bar stays relative to
            the largest value in the set, because a count has no natural percentage scale either.
          -->
          <h2 class="font-semibold mb-3">Departamentos con incidencias abiertas</h2>
          <div v-if="zonas.length === 0 && !loading" class="text-xs text-gray-500">Sin datos</div>
          <div v-for="z in zonas" :key="z.label" class="mb-4">
            <div class="flex justify-between text-sm mb-1">
              <span>{{ z.label }}</span>
              <span data-testid="superadmin-open-incidents-value">{{ z.value }} incidencias abiertas</span>
            </div>
            <div class="h-2 rounded-full bg-gray-200 overflow-hidden">
              <div class="h-full bg-gray-700" :style="{ width: zonaBarWidth(z.value) }"></div>
            </div>
          </div>
        </div>

        <div class="rounded-2xl border bg-white shadow-sm p-4">
          <!--
            P13-V5A-R5: el subtítulo prometía "pronósticos". R3 ya retiró la única tarjeta que
            proyectaba a futuro ("Proyección Incidencias"), de modo que las dos que quedan son
            agregados RETROSPECTIVOS derivados de lecturas reales. El encabezado ahora describe
            lo que hay; no se añade ningún pronóstico (el backend responde 501 a
            /api/analytics/predictive y /api/analytics/ai-predictions).
          -->
          <h2 class="font-semibold mb-2">📊 Indicadores derivados</h2>
          <p class="text-sm text-gray-600 mb-4">Agregados calculados sobre lecturas observadas</p>
          <div v-if="loading" class="grid grid-cols-3 gap-4">
            <div v-for="i in 3" :key="`derived-skeleton-${i}`" class="rounded-xl border p-3 bg-gray-50 animate-pulse">
              <div class="h-4 bg-gray-200 rounded mb-2"></div>
              <div class="h-6 bg-gray-300 rounded"></div>
              <div class="h-3 bg-gray-200 rounded mt-2"></div>
            </div>
          </div>
          <!--
            P13-V5A-R3 · "Proyección Incidencias" was RETIRED: `projectionPct` was hard-wired to 0
            and rendered as "↑ 0% — 30 días", a forecast with no forecaster behind it. A card
            without a producer is removed, not filled. The two that remain are derived from real
            reads and each renders an explicit absence when its input does not exist.
          -->
          <!--
            W2 · "SLA en Riesgo" IS REMOVED, NOT EMPTIED. It was `100 - avg(cumplimiento)` over
            `metas_cumplimiento`, a DEFERRED V8 SLA capability the pilot does not sell. Leaving the
            card in place rendering "Sin datos" would keep advertising SLA — §19 is explicit that
            the repair for an unsold field is replacement by a sold fact, not an empty placeholder
            that preserves the old promise. The replacement is `promedio_general`, an allowed
            measurement on the same signed route, in the hours the producer computes.
          -->
          <div v-else-if="derivedIndicators.hasData" class="grid grid-cols-2 gap-4">
            <!-- Tiempo Promedio de Resolución -->
            <div class="rounded-xl border p-3 text-center bg-gray-50" data-testid="derived-avg-resolution">
              <div class="text-sm text-gray-600">Tiempo Promedio de Resolución</div>
              <div v-if="derivedIndicators.avgResolutionHours !== null" class="text-xl font-semibold">
                {{ derivedIndicators.avgResolutionHours }} h
              </div>
              <div v-else class="text-xl font-semibold text-gray-400 italic">Sin datos</div>
              <div class="text-xs text-gray-500">sobre incidencias finalizadas</div>
            </div>
            <!-- Volumen -->
            <div class="rounded-xl border p-3 text-center bg-gray-50" data-testid="derived-cost-trend">
              <div class="text-sm text-gray-600">Tendencia de Volumen</div>
              <div v-if="derivedIndicators.costTrendPct !== null" class="text-xl font-semibold">
                {{ derivedIndicators.costTrendPct >= 0 ? '↑' : '↓' }} {{ Math.abs(derivedIndicators.costTrendPct) }}%
              </div>
              <div v-else class="text-xl font-semibold text-gray-400 italic">Sin datos</div>
              <div class="text-xs text-gray-500">primer vs. último periodo</div>
            </div>
          </div>
          <div v-else class="text-xs text-gray-500 border border-dashed border-gray-300 rounded-xl p-4 text-center">
            {{ derivedIndicators.message || 'Sin lecturas suficientes para calcular indicadores derivados.' }}
          </div>
        </div>
      </section>

      <!-- Acciones masivas / exportaciones -->
      <!--
        P13-V5A-R5 · RETIRADAS cuatro promesas de capacidad inertes.
        "Exportar CSV", "Exportar Excel", "Exportar PDF" y "Ver Reportes" eran cuatro <button>
        sin @click, sin :disabled y sin ninguna función asociada en el bloque de script: cuatro
        afirmaciones de capacidad que no hacían absolutamente nada al pulsarlas (§6 — un botón
        que nombra una capacidad inexistente es una afirmación de negocio). No se implementa
        aquí ninguna exportación: se retira la promesa y se apunta a la superficie de reportes
        organizacionales, que sí existe, está autorizada y exporta de verdad
        (/api/reports/organizational/export/{excel,pdf}/:reportType).
      -->
      <details class="rounded-2xl border bg-white shadow-sm p-4">
        <summary class="cursor-pointer font-semibold">Exportaciones y Acciones Masivas</summary>
        <div class="mt-4 text-sm text-gray-600" data-testid="superadmin-exports-retired">
          Este panel no dispone de exportación propia. Los reportes organizacionales
          —incluidas sus descargas en Excel y PDF— se publican en
          <RouterLink class="underline" to="/reports/organizational">Reportes Organizacionales</RouterLink>.
        </div>
      </details>

      <!-- P13-V7B · a design note addressed to whoever built the page was rendered to its user; it
           is gone. The load error stays. -->
      <div class="text-xs text-gray-400">
        <p v-if="error" class="text-red-500">{{ error }}</p>
      </div>
        </div>
      </div>
    </v-main>
  </v-layout>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import SidebarNavigation from '@/components/navigation/SidebarNavigation.vue'
import NotificationBell from '@/components/notifications/NotificationBell.vue'
import { useSuperAdminDashboard } from '@/composables/useSuperAdminDashboard'
import InteractiveMapMinimal from '@/views/maps/components/InteractiveMapMinimal.vue'

// Tipos y datos (alineados con la plantilla React)
type Tone = 'red' | 'purple' | 'blue' | 'green'

const toneStyles: Record<Tone, { bg: string; border: string; text: string }> = {
  // Tailwind approximations in hex/RGB to evitar purga
  red: { bg: '#FEF2F2', border: '#FECACA', text: '#DC2626' },
  purple: { bg: '#F5F3FF', border: '#DDD6FE', text: '#7C3AED' },
  blue: { bg: '#EFF6FF', border: '#BFDBFE', text: '#2563EB' },
  green: { bg: '#ECFDF5', border: '#A7F3D0', text: '#059669' },
}

// 🧭 Sidebar state
const sidebarCollapsed = ref(false)

// 📡 Datos reales del dashboard
const { loading, error, kpiCards, productividades, zonas, derivedIndicators, loadAll } = useSuperAdminDashboard()

/**
 * P13-V5A-R5 — the bar is comparative, not a percentage.
 * It previously used a raw ratio as a CSS width in `%`, which both mis-stated the unit and silently
 * clipped anything above 100. Widths are relative to the largest value present, so the bar compares
 * departments without asserting a scale the producer never defined. W2 changed the SERIES this bar
 * draws (open incidences, not workload per member); the reasoning is unchanged, because a count has
 * no natural percentage scale either.
 */
const zonaBarWidth = (value: number): string => {
  const values = zonas.value.map((z: { value: number }) => Number(z.value)).filter((v) => Number.isFinite(v))
  const max = values.length ? Math.max(...values) : 0
  if (!Number.isFinite(value) || max <= 0) return '0%'
  return `${Math.max(2, Math.round((Number(value) / max) * 100))}%`
}

onMounted(() => {
  loadAll()
})
</script>

<style scoped>
/* 🎨 Utility classes for spacing */
.space-y-6 > * + * {
  margin-top: 1.5rem;
}

.space-y-4 > * + * {
  margin-top: 1rem;
}

/* Fallback utilities (cuando Tailwind no esté cargado) */
.grid { display: grid; }
.grid-cols-1 { grid-template-columns: repeat(1, minmax(0, 1fr)); }
.grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.gap-3 { gap: 0.75rem; }
.gap-4 { gap: 1rem; }
.gap-6 { gap: 1.5rem; }
.rounded-xl { border-radius: 0.75rem; }
.rounded-2xl { border-radius: 1rem; }
.border { border-width: 1px; border-style: solid; border-color: #e5e7eb; }
.p-4 { padding: 1rem; }
.h-2 { height: 0.5rem; }
.h-3 { height: 0.75rem; }
.h-80 { height: 20rem; }
.shadow-sm { box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
.bg-white { background-color: #ffffff; }
.bg-gray-50 { background-color: #f9fafb; }
.bg-gray-200 { background-color: #e5e7eb; }
.text-center { text-align: center; }
.flex { display: flex; }
.items-center { align-items: center; }
.justify-center { justify-content: center; }
.justify-between { justify-content: space-between; }
.overflow-hidden { overflow: hidden; }
.min-h-screen { min-height: 100vh; }
.p-6 { padding: 1.5rem; }
.px-3 { padding-left: 0.75rem; padding-right: 0.75rem; }
.py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }

/* Responsive fallbacks */
@media (min-width: 640px) {
  .sm\:grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (min-width: 768px) {
  .md\:grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .md\:grid-cols-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
}
@media (min-width: 1024px) {
  .lg\:grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .lg\:col-span-2 { grid-column: span 2 / span 2; }
  .lg\:grid-cols-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
}
</style>
